"use client"
import { submitContactLead } from "@/lib/contact-lead"
import { Button } from "@/components/ui/button"
import { Form } from "@/components/ui/form"
import { useToast } from "@/components/ui/use-toast"
import { trackEvent } from "@/lib/tracking"
import confetti from "canvas-confetti"
import { CheckCircle2, Loader2 } from "lucide-react"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import ContactEmailField from "./form-fields/ContactEmailField"
import ContactMessageField from "./form-fields/ContactMessageField"
import ContactMobileField from "./form-fields/ContactMobileField"
import ContactNameField from "./form-fields/ContactNameField"
import ContactStoreField from "./form-fields/ContactStoreField"
import { useTranslations } from "next-intl"

interface ContactFormData {
  storeName: string;
  email: string;
  name: string;
  mobile: string;
  message?: string;
}

export type ContactFormFieldConfig = {
  fieldType: string;
  label?: string | null;
  required?: boolean;
};

export interface ContactFormConfig {
  title?: string | null;
  intro?: string | null;
  submitLabel?: string | null;
  sendingLabel?: string | null;
  toastTitle?: string | null;
  toastDescription?: string | null;
  fields?: ContactFormFieldConfig[] | null;
}

const DEFAULT_FIELDS: ContactFormFieldConfig[] = [
  { fieldType: "storeName", required: true },
  { fieldType: "email", required: true },
  { fieldType: "name", required: true },
  { fieldType: "mobile", required: true },
  { fieldType: "message", required: false },
]

interface ContactFormProps {
  onClose: () => void;
  hubspotMeetingLink?: {
    url: string;
    label: string;
    enabled: boolean | null;
  } | null;
  contactForm?: ContactFormConfig | null;
}

const FIELD_COMPONENTS = {
  storeName: ContactStoreField,
  email: ContactEmailField,
  name: ContactNameField,
  mobile: ContactMobileField,
  message: ContactMessageField,
} as const

function isContactFormFieldType(
  s: string
): s is keyof typeof FIELD_COMPONENTS {
  return s in FIELD_COMPONENTS
}

const SUCCESS_AUTO_CLOSE_MS = 2500

const ContactForm = ({
  onClose,
  hubspotMeetingLink,
  contactForm,
}: ContactFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const { toast } = useToast()
  const t = useTranslations("ContactForm")
  const form = useForm<ContactFormData>({
    defaultValues: {
      storeName: "",
      email: "",
      name: "",
      mobile: "",
      message: "",
    },
  })

  const intro = (contactForm?.intro?.trim()) || t("intro")
  const submitLabel = (contactForm?.submitLabel?.trim()) || t("send")
  const sendingLabel = (contactForm?.sendingLabel?.trim()) || t("sending")
  const toastTitle = (contactForm?.toastTitle?.trim()) || t("toast.title")
  const toastDescription =
    (contactForm?.toastDescription?.trim()) || t("toast.description")

  const fieldConfigs =
    contactForm?.fields && contactForm.fields.length > 0
      ? (() => {
          const mapped = contactForm.fields.map((f) => ({
            fieldType: f.fieldType,
            label: (f.label?.trim()) || undefined,
            required:
              f.fieldType === "message"
                ? f.required ?? false
                : f.required ?? true,
          }))
          // Dedupe by fieldType so we never render the same form field twice
          const seen = new Set<string>()
          return mapped.filter((c) => {
            if (seen.has(c.fieldType)) return false
            seen.add(c.fieldType)
            return true
          })
        })()
      : DEFAULT_FIELDS

  const onSubmit = async (data: ContactFormData) => {
    try {
      setIsSubmitting(true)

      // `mobile` used to be folded into the message body because Medusa's
      // validator rejected it as an unrecognised field. /api/contact takes it as
      // a first-class field, so it is sent as one and rendered on its own line.
      const result = await submitContactLead({
        storeName: data.storeName,
        email: data.email,
        name: data.name,
        mobile: data.mobile,
        message: data.message,
        source: "contact_form",
      })

      if (!result.ok) {
        throw new Error(result.errors.join("; "))
      }

      trackEvent("Contact Form Submitted", {
        source: "contact_form",
        success: true,
      })

      toast({
        title: toastTitle,
        description: toastDescription,
      })

      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      })

      form.reset()
      setSubmitSuccess(true)
    } catch (error) {
      console.error("Failed to send message:", error)
      trackEvent("Contact Form Submitted", {
        source: "contact_form",
        success: false,
      })
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    if (!submitSuccess) return
    const timer = window.setTimeout(() => {
      onClose()
    }, SUCCESS_AUTO_CLOSE_MS)
    return () => window.clearTimeout(timer)
  }, [submitSuccess, onClose])

  const handleCloseAfterSuccess = () => {
    setSubmitSuccess(false)
    onClose()
  }

  if (submitSuccess) {
    return (
      <div className="py-6 space-y-6" data-testid="contact-form-success">
        <div className="flex flex-col items-center text-center space-y-4">
          <CheckCircle2 className="h-14 w-14 text-green-600" aria-hidden />
          <div className="space-y-2">
            <h3 className="text-lg font-semibold">{toastTitle}</h3>
            <p className="text-sm text-muted-foreground">{toastDescription}</p>
          </div>
        </div>
        <Button
          type="button"
          className="w-full button-primary"
          onClick={handleCloseAfterSuccess}
          data-testid="contact-form-close-after-success"
        >
          {t("close")}
        </Button>
      </div>
    )
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-6 py-4"
        data-testid="contact-form"
      >
        {hubspotMeetingLink?.enabled !== false && hubspotMeetingLink?.url && (
          <div className="text-center mb-8">
            <p className="text-sm text-gray-600 mb-4">{intro}</p>
            <a
              href={hubspotMeetingLink.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-black hover:bg-gray-800 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
              onClick={() => {
                trackEvent("Book Demo Directly Clicked", {
                  source: "contact_form",
                  link_url: hubspotMeetingLink.url,
                  link_label: hubspotMeetingLink.label,
                })
              }}
              data-testid="contact-form-book-demo-link"
            >
              {hubspotMeetingLink.label}
            </a>
          </div>
        )}

        {fieldConfigs.map((config) => {
          if (!isContactFormFieldType(config.fieldType)) return null
          const FieldComponent = FIELD_COMPONENTS[config.fieldType]
          return (
            <FieldComponent
              key={config.fieldType}
              form={form}
              label={config.label}
              required={config.required}
            />
          )
        })}

        <Button
          type="submit"
          className="w-full button-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {sendingLabel}
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </form>
    </Form>
  )
}

export default ContactForm
